import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation, OnChanges, SimpleChanges, OnDestroy, HostListener, ViewChild, ElementRef } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ZuperDialogService } from '@zuper/components/zuper-dialog/zuper-dialog.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { ContactAddress } from 'app/interfaces/google-maps-autocomplete.inerface';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { MapPickerComponent } from '../map-picker/map-picker.component';
import { MapPickerService } from '../map-picker/map-picker.service';
import { ContactInfoService } from './contact-info.component.service';
import { ContactFields, countries, stateCityData } from './contact-info.interface';
import PlaceResult = google.maps.places.PlaceResult;
import { UtilityService } from '../../core/services/utility_functions/utility_functions.service';
import { LocalStorageService } from 'app/core/services/localstorage.service';
import { addressTypes } from 'app/modules/quotations/quote-invoice-form/quote-invoice-form.types';
import { emailPattern } from './contact-info.interface';
@Component({
  selector: 'contact-info',
  templateUrl: './contact-info.component.html',
  styleUrls: ['./contact-info.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ContactInfoComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('streetInput', { read: ElementRef }) streetInput: ElementRef;

  @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
  @Output() regionalChange: EventEmitter<stateCityData> = new EventEmitter();

  @Input() fields: ContactFields = null;
  @Input() addressFormGroupName: string;
  @Input() disableAll: boolean;
  @Input() optionalAddress: boolean;
  @Input() allowMulitpleEmails: boolean = false;

  addressFormGroup: UntypedFormGroup = null;
  countries: any[] = countries;
  filteredOptions: Observable<any[]>;
  mapPickerSubscription: Subscription;
  addressSubscription: Subscription;
  formControls: any[] = [{
    formControlName: 'firstName',
    value: '',
    validators: []
  }, {
    formControlName: 'lastName',
    value: '',
    validators: []
  }, {
    formControlName: 'email',
    value: '',
    validators: [Validators.email]
  }, {
    formControlName: 'phone',
    value: '',
    validators: []
  }, {
    formControlName: 'street',
    value: '',
    validators: [Validators.required]
  }, {
    formControlName: 'landmark',
    value: '',
    validators: []
  }, {
    formControlName: 'city',
    value: '',
    validators: [Validators.required]
  }, {
    formControlName: 'state',
    value: '',
    validators: []
  }, {
    formControlName: 'country',
    value: '',
    validators: []
  }, {
    formControlName: 'zipcode',
    value: '',
    validators: []
  }, {
    formControlName: 'latitude',
    value: null,
    validators: []
  }, {
    formControlName: 'longitude',
    value: null,
    validators: []
  }];
  cityBehaviouralSubject = new BehaviorSubject<stateCityData>(null);
  stateBehaviouralSubject = new BehaviorSubject<stateCityData>(null);
  citySubscription: Subscription;
  stateSubscription: Subscription;
  companyConfig: any;
  company: any;
  isStreetInputFocused = false; // Track focus state of the street input

  constructor(
    private localisationService: LocalisationService,
    private rootFormGroup: FormGroupDirective,
    private contactInfoService: ContactInfoService,
    private zuperDialogService: ZuperDialogService,
    private utilityService: UtilityService,
    private _mapPickerService: MapPickerService,
    private localStorageService: LocalStorageService,
    ){
      this.companyConfig = this.localStorageService.getLocalStorage('company_configuration_data');
      this.company = this.localStorageService.getLocalStorage('company');
  }

  getMapPickerData(): void {
    this.mapPickerSubscription = this._mapPickerService.getAddress
      .subscribe((res: any) => {
        if(['serviceAddress', 'propertyAddress', 'newAddress', 'jobLocation'].includes(res?.formGroupName)){
          const currentFormGroup = this.addressFormGroup.parent.controls[res?.formGroupName] as UntypedFormGroup;
          currentFormGroup.patchValue(res?.value);
          if(!this.utilityService.isEmptyOrUndefined(res?.value?.geoLocation?.latitude)){
            currentFormGroup.get('latitude').setValue(res?.value?.geoLocation?.latitude);
          }
          if(!this.utilityService.isEmptyOrUndefined(res?.value?.geoLocation?.longitude)){
            currentFormGroup.get('longitude').setValue(res?.value?.geoLocation?.longitude);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.mapPickerSubscription.unsubscribe();
    this.addressSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    this.localisationService.setDefaultLanguage();
    this.addressFormGroup = this.rootFormGroup.control.get(this.addressFormGroupName) as UntypedFormGroup;
    this.addAddressFormControls();
    this.filteredOptions = this.addressFormGroup.get('country').valueChanges.pipe(
      startWith(''),
      map(value => this.countryFilter(value)),
    );
    this.addressFormGroup.get('street')?.valueChanges?.subscribe((value: any) => {
      if (!value && this.fields.enableMapsAutoComplete) {
        this.addressFormGroup.patchValue({
          latitude: null,
          longitude: null
        });
      }
      this.updateCoordinatesFields();
    });
    if(this.addressFormGroup?.controls && this.optionalAddress){
      this.addressFormGroup.get('street').removeValidators([Validators.required]);
      this.addressFormGroup.get('city').removeValidators([Validators.required]);
    }else{
      this.addressFormGroup.get('street').addValidators([Validators.required]);
      this.addressFormGroup.get('city').addValidators([Validators.required]);
    }
    this.addressSubscription = this.contactInfoService.address.subscribe((data: any)=>{
      if(data){
        const address = {...data,country:this.countries.find((cnt: any)=>cnt.label === data.country)};
        this.addressFormGroup.patchValue(address);
      }
    });

    this.getMapPickerData();
    this.initStateCitySubscription();
  }

  getStreetInput(): ElementRef {
    return this.streetInput;
  }

  onStreetInputFocus(): void {
    this.isStreetInputFocused = true;
  }

  onStreetInputBlur(): void {
    this.isStreetInputFocused = false;
  }

  displayFn(country: any): string {
    return country && country.label ? country.label : '';
  }

  countryFilter(value: any): any[] {
    value = value ? (value?.label ? value?.label : value?.value ?? value) : '';
    const filterValue = value?.toLowerCase();
    return this.countries.filter(option => option.label.toLowerCase().includes(filterValue));
  }

  addAddressFormControls(): void {
    this.formControls.forEach((control: any) => {
      // Custom email validation depending on the allowMultipleEmails flag
      if (control?.formControlName === 'email' && this.allowMulitpleEmails) {
        control.validators = [this.validateMultipleEmails()];
      }
      if (this.addressFormGroup?.controls?.[control?.formControlName]) {
        control.value = this.addressFormGroup.value?.[control?.formControlName];
        this.addressFormGroup.removeControl(control?.formControlName);
      }
      this.addressFormGroup.addControl(control.formControlName, new UntypedFormControl(control.value, control.validators));
    });
  }

  showError(formControlName: string): boolean {
    const formControl: AbstractControl = this.addressFormGroup.get(formControlName);
    if (formControl) {
      return !formControl.disabled && formControl.touched && !formControl.valid;
    }
    return false;
  }

  updateCoordinatesFields(): void {
    if (this.fields.enableMapsAutoComplete && this.fields?.disbleCoOrdinatesBasedOnValue) {
      ['latitude', 'longitude'].forEach((formControlName: string) => {
        const formControl: AbstractControl = this.addressFormGroup.get(formControlName);
        if (!this.utilityService.isEmptyOrUndefined(formControl?.value)) {
          formControl.disable();
          formControl.updateValueAndValidity();
        } else {
          formControl.enable();
          formControl.updateValueAndValidity();
        }
      });
    }
  }

  showCoordinatesField(formControlName: string): boolean {
    if (this.fields.enableMapsAutoComplete && this.fields?.disbleCoOrdinatesBasedOnValue) {
      const formControl: AbstractControl = this.addressFormGroup.get(formControlName);
      return formControl && !this.utilityService.isEmptyOrUndefined(formControl?.value);
    }
    return true;
  }

  getElementID(controllName: string): string {
    return this.addressFormGroupName + '_' + controllName;
  }

  onAutocompleteSelected(result: { result: PlaceResult; address: ContactAddress}): void {
    if (result.address) {
      const address: any = {
        street: result.address.street,
        city: result.address.city,
        state: result.address.state ?? '',
        country: result.address.country ?? '',
        zipcode: result.address.zipcode ?? '',
        latitude: result.address.geoLocation.latitude,
        longitude: result.address.geoLocation.longitude
      };
      this.addressFormGroup.patchValue(address);
      this.updateCoordinatesFields();
    }
  }

  openMap(): void {
    const mapPicker: MatDialogRef<MapPickerComponent> = this.zuperDialogService.openCustom(MapPickerComponent,
      {
          extraClass: 'map-picker',
          data: {
            formGroupName: this.addressFormGroupName
          }
      });
    const instance = mapPicker.componentInstance;
    instance.standalone = false;
  }

  initStateCitySubscription(): void{
    this.citySubscription = this.cityBehaviouralSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value)=>{
      if(value !== null){
        this.regionalChange.emit(value);
      }
    });
    this.stateSubscription = this.stateBehaviouralSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value)=>{
      if(value !== null){
        this.regionalChange.emit(value);
      }
    });
  }

  emitRegionalChange($event ,fieldName: string, addressFormGroupName: string): void{
    const value = $event.target.value;
        const data: stateCityData = {
          fieldValue: value,
          fieldName: fieldName,
          addressType: addressFormGroupName
        };
        if(fieldName === 'CITY'){
          this.cityBehaviouralSubject.next(data);
        }
        if(fieldName === 'STATE'){
          this.stateBehaviouralSubject.next(data);
        }
  }

  // Custom validator for handling multiple or single email validation
  validateMultipleEmails() {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const emailValue = control.value;
      if (!emailValue)
      {
        return null;
      }

      const allEmails = emailValue.split(',').map(email => email.trim());
      const invalidEmails = allEmails.filter(email => !emailPattern.test(email));

      if (invalidEmails.length > 0) {
        return { invalidMultipleEmails: true }; // Error if any email is invalid
      }
      return null;
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if('disableAll' in changes){
      if(this.addressFormGroup?.controls){
        if (this.disableAll) {
          this.addressFormGroup.markAsUntouched();
        }
        for(const [key, formControl] of Object.entries(this.addressFormGroup.controls)){
          if(this.disableAll){
            formControl.disable();
          }else{
            formControl.enable();
          }
        }
      }
    }
    if('optionalAddress' in changes){
      if(this.addressFormGroup?.controls && this.optionalAddress){
        this.addressFormGroup?.get('street')?.removeValidators([Validators.required]);
        this.addressFormGroup?.get('city')?.removeValidators([Validators.required]);
      }else{
        this.addressFormGroup?.get('street')?.addValidators([Validators.required]);
        this.addressFormGroup?.get('city')?.addValidators([Validators.required]);
      }
      this.addressFormGroup?.get('street')?.updateValueAndValidity();
      this.addressFormGroup?.get('street')?.updateValueAndValidity();
    }
  }

}
